import React, { useEffect, useState } from 'react';
import {
    Container, Typography, Grid, Card, CardContent, Chip, Box, Stack, Tooltip
} from '@mui/material';

// Icons
import BuildIcon from '@mui/icons-material/Build';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';

// FIREBASE IMPORTS
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../firebase";

const Roadmap = () => {
  const [items, setItems] = useState([]);
  const auth = getAuth();

  // 1. Fetch Roadmap Items
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
        try {
            const querySnapshot = await getDocs(collection(db, "feedback"));
            
            const fetchedItems = querySnapshot.docs.map(docSnap => {
                const data = docSnap.data();
                const upvotedBy = data.upvoted_by || [];
                
                return {
                    id: docSnap.id,
                    ...data,
                    // The total upvotes is just the length of the array
                    upvotes: upvotedBy.length,
                    // Check if the current user's ID is in that array
                    has_upvoted: user ? upvotedBy.includes(user.uid) : false
                };
            });

            // Sort by upvotes descending
            const sorted = fetchedItems.sort((a, b) => b.upvotes - a.upvotes);
            setItems(sorted);
        } catch (err) {
            console.error("Failed to load roadmap", err);
        }
    });

    return () => unsubscribe();
  }, [auth]);

  const getItemsByStatus = (status) => items.filter(i => i.status === status);

  // 2. Handle Upvoting Logic
  const handleUpvote = async (id) => {
      const user = auth.currentUser;
      if (!user) {
          alert("Please login to vote!");
          return;
      }

      // Find the item being voted on
      const itemIndex = items.findIndex(i => i.id === id);
      const item = items[itemIndex];
      const wasUpvoted = item.has_upvoted;

      // Optimistic UI Update (instant feedback)
      const updatedItems = [...items];
      updatedItems[itemIndex] = {
          ...item,
          has_upvoted: !wasUpvoted,
          upvotes: wasUpvoted ? item.upvotes - 1 : item.upvotes + 1
      };
      
      // Re-sort the list instantly so highly voted items jump to the top
      updatedItems.sort((a, b) => b.upvotes - a.upvotes);
      setItems(updatedItems);

      // Database Update
      try {
          const docRef = doc(db, "feedback", id);
          if (wasUpvoted) {
              await updateDoc(docRef, { upvoted_by: arrayRemove(user.uid) });
          } else {
              await updateDoc(docRef, { upvoted_by: arrayUnion(user.uid) });
          }
      } catch (err) {
          console.error("Failed to upvote", err);
          alert("Failed to save your vote. Please try again.");
          // Ideally, revert the state here if the database fails
      }
  };

  // Reusable Column Component
  const StatusColumn = ({ title, status, icon, color, items }) => (
    <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: '300px' }}>
        <Box sx={{
            bgcolor: '#f4f5f7',
            borderRadius: 3,
            p: 2,
            height: '100%',
            width: '100%',
            minHeight: 500,
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                mb: 3, pb: 1, borderBottom: `3px solid ${color}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#333' }}>
                    {React.cloneElement(icon, { sx: { color: color } })}
                    <Typography variant="h6" fontWeight="bold">{title}</Typography>
                </Box>
                <Chip label={items.length} size="small" sx={{ bgcolor: 'white', fontWeight: 'bold', color: '#666', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} />
            </Box>

            {/* Cards Stack */}
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
                {items.map(item => (
                    <Card key={item.id} sx={{
                        borderRadius: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        border: '1px solid transparent',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', borderColor: color }
                    }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            {/* Tags and Vote Row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Chip
                                    icon={item.type === 'bug' ? <BugReportIcon style={{ fontSize: 14 }} /> : <LightbulbIcon style={{ fontSize: 14 }} />}
                                    label={item.type === 'bug' ? 'Bug Fix' : 'Feature'}
                                    size="small"
                                    sx={{
                                        height: 24, fontSize: '0.75rem', fontWeight: 'bold',
                                        bgcolor: item.type === 'bug' ? '#ffebee' : '#e3f2fd',
                                        color: item.type === 'bug' ? '#c62828' : '#1565c0', border: 'none'
                                    }}
                                />

                                {/* UPVOTE BUTTON */}
                                <Tooltip title={item.has_upvoted ? "Remove Vote" : "Upvote this feature"}>
                                    <Box
                                        onClick={() => handleUpvote(item.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                            cursor: 'pointer', opacity: 0.8,
                                            '&:hover': { opacity: 1, color: color }
                                        }}
                                    >
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            {item.upvotes || 0}
                                        </Typography>
                                        {item.has_upvoted ?
                                            <ThumbUpIcon fontSize="small" sx={{ color: color, fontSize: 18 }} /> :
                                            <ThumbUpOutlinedIcon fontSize="small" sx={{ fontSize: 18 }} />
                                        }
                                    </Box>
                                </Tooltip>
                            </Box>

                            <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 1 }}>
                                {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                {item.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {items.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', opacity: 0.4 }}>
                    <Typography variant="body2" fontStyle="italic">No items yet</Typography>
                </Box>
            )}
        </Box>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 6, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h3" gutterBottom sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
          Product Roadmap
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Check out what we have planned for the Astrup Family Cookbook. <br/>
            Have a suggestion? Submit feedback using the feedback button in the bottom right.
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="stretch" wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
        <StatusColumn title="Planned" status="planned" icon={<EventNoteIcon />} color="#FF9800" items={getItemsByStatus('planned')} />
        <StatusColumn title="In Progress" status="in_progress" icon={<BuildIcon />} color="#2196F3" items={getItemsByStatus('in_progress')} />
        <StatusColumn title="Completed" status="completed" icon={<CheckCircleIcon />} color="#4CAF50" items={getItemsByStatus('completed')} />
      </Grid>
    </Container>
  );
};

export default Roadmap;